import React, { useCallback } from 'react';

import { ListRenderItemInfo, View } from 'react-native';

import { useTranslation } from 'react-i18next';
import { SafeAreaView } from 'react-native-safe-area-context';

import { CloseIcon } from 'assets/images';
import CheckMarkImage from 'assets/images/svgs/ico_checkmark.svg';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import gStyles from 'config/styles';
import {
    AUDIO_LANGUAGE_SECTION_TITLE,
    SUBTITLES_SECTION_TITLE,
    SUBTITLE_AUDIO_OVERLAY_CLOSE_BUTTON,
} from 'config/testID';

import { PlayerTrack } from 'store/player/reducer.types';

import SvgButton from 'components/CustomButtons/SvgButton';

import { IProps } from '../types';
import Section from './components/Section';
import SelectionOption from './components/SelectionOption';
import styles from './styles';

const {
    SCREEN_AUDIO_SUBTITLES_SUBTITLES,
    SCREEN_AUDIO_SUBTITLES_AUDIO_LANGUAGE,
    CLOSE_BUTTON,
} = LANGUAGE_KEYS;

function SubtitleAudioOverlay({
    onTextTrackPress,
    onAudioTrackPress,
    selectedAudioTrack,
    selectedTextTrack,
    keyExtractor,
    onClose,
    isVisible,
    audioTracks = [],
    textTracks = [],
}: IProps) {
    const { t } = useTranslation();

    const renderAudioItem = useCallback(
        ({ item }: ListRenderItemInfo<PlayerTrack>) => {
            const isSelected = item.id === selectedAudioTrack?.id;

            return (
                <SelectionOption
                    key={keyExtractor(item)}
                    item={item}
                    onPress={onAudioTrackPress}
                    isSelected={isSelected}
                    SvgRightImage={isSelected ? CheckMarkImage : null}
                    isAudio
                />
            );
        },
        [keyExtractor, onAudioTrackPress, selectedAudioTrack?.id],
    );

    const renderTextItem = useCallback(
        ({ item }: ListRenderItemInfo<PlayerTrack>) => {
            const isSelected = item.id === selectedTextTrack?.id;

            return (
                <SelectionOption
                    key={keyExtractor(item)}
                    item={item}
                    onPress={onTextTrackPress}
                    isSelected={isSelected}
                    SvgRightImage={isSelected ? CheckMarkImage : null}
                />
            );
        },
        [keyExtractor, onTextTrackPress, selectedTextTrack?.id],
    );

    const audioTrackProps = {
        keyExtractor,
        renderItem: renderAudioItem,
        data: audioTracks,
        title: t(SCREEN_AUDIO_SUBTITLES_AUDIO_LANGUAGE),
        showsVerticalScrollIndicator: false,
        bounces: false,
    };

    const textTrackProps = {
        keyExtractor,
        renderItem: renderTextItem,
        data: textTracks,
        title: t(SCREEN_AUDIO_SUBTITLES_SUBTITLES),
        showsVerticalScrollIndicator: false,
        bounces: false,
    };

    if (!isVisible) {
        return null;
    }

    return (
        <View style={styles.root}>
            <SafeAreaView style={styles.safeArea}>
                <View style={gStyles.flex}>
                    <View style={styles.closeContainer}>
                        <SvgButton
                            accessible
                            accessibilityLabel={t(CLOSE_BUTTON)}
                            accessibilityRole="button"
                            Svg={CloseIcon}
                            style={styles.close}
                            svgStyle={styles.closeSvg}
                            onPress={onClose}
                            testID={SUBTITLE_AUDIO_OVERLAY_CLOSE_BUTTON}
                        />
                    </View>
                    <View style={styles.contentWrapper}>
                        {audioTrackProps.data.length > 1 && (
                            <Section
                                {...audioTrackProps}
                                testID={AUDIO_LANGUAGE_SECTION_TITLE}
                            />
                        )}
                        {!!textTrackProps.data.length && (
                            <Section
                                {...textTrackProps}
                                testID={SUBTITLES_SECTION_TITLE}
                            />
                        )}
                    </View>
                </View>
            </SafeAreaView>
        </View>
    );
}

export default React.memo(SubtitleAudioOverlay);
